import React, { useCallback } from "react";
import { useNavigate } from "react-router";

import { DecodableApiError } from "decodable-sdk-js/v1alpha2";

import { useSnackbar } from "../../components/SnackbarProvider";
import { useFeatureFlags } from "../../components/experiments";
import { sample } from "../../lib/util";
import { defaultCloud } from "../constants";
import { useAvailableCells, useCreateAccount } from "../hooks/queries";
import { useAccountCreatePageState } from "../hooks/useAccountCreatePageState";
import { useAccounts } from "../hooks/useAccounts";
import { selectDefaultRegion } from "../utils";
import { AccountCreateForm } from "./AccountCreateForm";
import { AccountCreatePage } from "./AccountCreatePage";
import { useAppEventDispatch } from "../../hooks/useAppEvents";

// AccountCreateController binds the AccountCreatePage to the hook.
// This just provides some decoupling for testing.
export function AccountCreateContainer({ cellSampleFunc = sample }) {
  const showSnackbar = useSnackbar();
  const dispatchAppEvent = useAppEventDispatch();
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();

  const { plan, selectPlan, formState, dispatch } = useAccountCreatePageState();

  const { data: cells, refetch: refetchCells } = useAvailableCells({
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    onError(error) {
      dispatch({
        type: "updateRegions",
        error,
        loading: false,
      });
    },
    onSuccess(cells) {
      dispatch({
        type: "updateRegions",
        error: null,
        regions: Object.keys(cells?.[defaultCloud] ?? {}),
        loading: false,
      });

      dispatch({
        type: "updateForm",
        formData: {
          selectedRegion: selectDefaultRegion(cells),
        },
      });
    },
  });

  const onRefetchRegions = useCallback(() => {
    dispatch({
      type: "updateRegions",
      error: null,
      loading: true,
    });
    return refetchCells();
  }, [refetchCells]);

  const { refresh: refreshAccounts } = useAccounts();

  const { mutateAsync, isLoading: isCreating } = useCreateAccount();

  async function handleCreateAccount() {
    try {
      const cell = cellSampleFunc(
        cells[defaultCloud][formState.selectedRegion]
      );

      await mutateAsync({
        cell,
        plan,
        name: formState.name,
        displayName: formState.displayName,
        invites: formState.invites,
      });

      dispatchAppEvent("account:create", {
        cell,
        plan_id: plan.id,
        name: formState.name,
      });
    } catch (ex) {
      if (ex instanceof DecodableApiError) {
        // status: conflict
        if (ex.status === 409) {
          dispatch({
            type: "updateForm",
            formData: {
              isNameConflict: true,
            },
          });
          return;
        }
      }
      showSnackbar({
        severity: "error",
        title: "Error creating account",
        message: "Please try again",
      });
      return;
    }

    showSnackbar({
      severity: "success",
      title: "Account Created!",
      message: `Redirecting to new account: ${formState.name}`,
    });

    try {
      await refreshAccounts();

      const destination = `/${formState.name}`;

      navigate(destination);
    } catch (ex) {
      // todo: Maybe just do this?
      document.location = `/${formState.name}`;
    }
  }

  return (
    <AccountCreatePage
      plan={plan}
      onSelectPlan={selectPlan}
      form={
        <AccountCreateForm
          onRefetchRegions={onRefetchRegions}
          plan={plan}
          formState={formState}
          dispatch={dispatch}
          isCreating={isCreating}
          onCreateAccount={handleCreateAccount}
        />
      }
    />
  );
}
